// filepath: apps/web/src/features/proc/useRetryStep.ts
//
// FR-UI-PROC-1 AC-5 — TanStack Query mutation hook for
// POST /api/processes/instances/{id}/retry-step.
//
// Mirrors the FR-UI-PROM-2 mutation pattern (useApprovePromotion.ts
// + useRejectPromotion.ts) and the FR-UI-TASK-1 mutation pattern
// (useCompleteTask.ts):
//   - generates an idempotency_key via `crypto.randomUUID()` per
//     ARCH Rule 3 (the same value MUST be sent on every retry of
//     the same user-intent so the server can dedup);
//   - sends the body as `{idempotency_key}` only (no tenant
//     secrets per INV-5);
//   - invalidates `procKeys.failedInstances` on success so the
//     dashboard row transitions out of `failed` automatically.
//   - HTTP 409 `not_failed` (FR-BPM-14 `ErrInstanceNotFailed`) is
//     surfaced as a typed ApiError so the row component can render
//     the FR-UI-CORE-10 `stale-version` state (the instance is no
//     longer in `failed` — a refresh-required condition).
//   - No auto-retry on 409 (FR-BPM-14 explicit) or 410 (terminal).
//
// Cache-key shape: the mutation hook does not subscribe to a query
// key itself. It invalidates procKeys.failedInstances(tenantSlug)
// on success — same tenant-scoped shape as the list hook.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/definitions/types";
import { useTenant } from "@/tenant/TenantProvider";
import { apiFetch } from "@/api";
import { procKeys, type RetryStepSuccess } from "./types";

interface RetryStepVariables {
  instanceId: string;
}

/**
 * Options for useRetryStep.
 *   - onConflict: called when server returns 409 `not_failed` — the
 *     caller can surface the FR-UI-CORE-10 `stale-version` state
 *     (the row is no longer in `failed`; refresh required).
 *   - onError: called for any non-conflict mutation error.
 *   - onSuccess: called on 2xx — the caller may want to navigate /
 *     announce.
 */
export interface UseRetryStepOptions {
  onConflict?: (err: ApiError) => void;
  onError?: (err: ApiError) => void;
  onSuccess?: (result: RetryStepSuccess) => void;
}

export interface UseRetryStepReturn {
  mutate: (variables: RetryStepVariables) => void;
  mutateAsync: (
    variables: RetryStepVariables,
  ) => Promise<RetryStepSuccess | undefined>;
  isPending: boolean;
  isError: boolean;
  error: ApiError | null;
}

export function useRetryStep(
  opts: UseRetryStepOptions = {},
  apiBase = "/api",
): UseRetryStepReturn {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();

  const mutation = useMutation<RetryStepSuccess, ApiError, RetryStepVariables>({
    mutationFn: async ({ instanceId }: RetryStepVariables) => {
      const url = `${apiBase}/processes/instances/${encodeURIComponent(
        instanceId,
      )}/retry-step`;
      // ARCH Rule 3 — generate idempotency_key client-side via
      // crypto.randomUUID(). Mirrors useApprovePromotion.ts:25.
      const idempotencyKey = crypto.randomUUID();

      let response: Response;
      try {
        // ISS-377 round 3 R2-B2.5: migrated to apiFetch. The
        // body field `idempotency_key` is part of the server
        // contract (ARCH Rule 3); the apiFetch `idempotencyKey`
        // flag is intentionally NOT passed (the action
        // handler reads the body field, not the X-Idempotency-Key
        // header) so a redundant header is not attached.
        response = await apiFetch(url, {
          method: "POST",
          init: {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({ idempotency_key: idempotencyKey }),
          },
        });
      } catch (networkErr) {
        throw new ApiError(
          networkErr instanceof Error ? networkErr.message : "network error",
          0,
          "network_error",
        );
      }

      if (response.status === 409) {
        // FR-BPM-14 ErrInstanceNotFailed → server-side code
        // `not_failed`. Throw a typed ApiError so the caller can
        // map to the FR-UI-CORE-10 stale-version surface.
        const body = await safeReadJson(response);
        throw new ApiError(
          (body as { error?: string } | null)?.error ??
            "instance is not in failed state",
          409,
          "not_failed",
        );
      }

      if (!response.ok) {
        const body = await safeReadJson(response);
        const code =
          (body as { code?: string } | null)?.code ?? `http_${response.status}`;
        throw new ApiError(
          (body as { error?: string } | null)?.error ?? response.statusText,
          response.status,
          code,
        );
      }

      return (await response.json()) as RetryStepSuccess;
    },

    onSuccess: (result, variables) => {
      // Invalidate the failed-instance list so the row transitions
      // out of the dashboard. Also invalidate the trace view so
      // a follow-up visit shows the new step-log entries.
      void queryClient.invalidateQueries({
        queryKey: procKeys.failedInstances(tenantSlug),
      });
      void queryClient.invalidateQueries({
        queryKey: procKeys.stepLog(tenantSlug, variables.instanceId),
      });
      opts.onSuccess?.(result);
    },

    onError: (err) => {
      if (err.code === "not_failed" && err.status === 409) {
        // Surface the typed conflict to the row component — the
        // caller renders the FR-UI-CORE-10 stale-version state.
        opts.onConflict?.(err);
        return;
      }
      opts.onError?.(err);
    },

    retry: (count, err) => {
      // Never auto-retry 409 (user must pick a resolution action)
      // or any 4xx (caller decides whether to retry).
      if (err instanceof ApiError) {
        if (err.status >= 400 && err.status < 500) return false;
      }
      return count < 2;
    },
  });

  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  };
}

async function safeReadJson(response: Response): Promise<unknown> {
  try {
    return (await response.json()) as unknown;
  } catch {
    return null;
  }
}
